import React from "react";
import {
  parseMentionText,
  type MentionUser,
  type MentionPart,
  PRISM7_MENTION_USER,
} from "../helpers/parseMentionText";

/** Props for MentionText: text, user list, optional click handler and styling. */
export interface MentionTextProps {
  /** Raw message text that may contain @mentions. */
  text: string;
  /** Users to resolve mentions from (id + displayName); no hardcoded names. */
  userObjects: MentionUser[];
  /** Called when a mention is clicked (id, displayName). */
  onMentionClick?: (id: string, displayName: string) => void;
  /** Optional inline style applied to mention spans. */
  mentionStyle?: React.CSSProperties;
  /** Optional class name for the root wrapper. */
  className?: string;
  /** Optional style for the root wrapper. */
  style?: React.CSSProperties;
}

/** Default styles for mention-highlight (blue, bold, pointer). */
const defaultMentionStyle: React.CSSProperties = {
  color: "#1976d2",
  fontWeight: 600,
  cursor: "pointer",
};

/**
 * Renders text with @mentions as highlighted, clickable spans.
 * Uses parseMentionText for longest-match and punctuation handling.
 */
const MentionText: React.FC<MentionTextProps> = ({
  text,
  userObjects,
  onMentionClick,
  mentionStyle,
  className,
  style,
}) => {
  const parts: MentionPart[] = parseMentionText(text, userObjects);
  const resolvedMentionStyle = { ...defaultMentionStyle, ...mentionStyle };

  return (
    <span className={className} style={style}>
      {parts.map((part, index) => {
        if (typeof part === "string") {
          return <React.Fragment key={index}>{part}</React.Fragment>;
        }
        const displayName = part.name.slice(1);
        return (
          <span
            key={index}
            className="mention-highlight"
            role="button"
            tabIndex={0}
            style={resolvedMentionStyle}
            onClick={() => onMentionClick?.(part.id, displayName)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onMentionClick?.(part.id, displayName);
              }
            }}
          >
            {part.name}
          </span>
        );
      })}
    </span>
  );
};

export default MentionText;
export { PRISM7_MENTION_USER };
